// src/components/dnl/DNLToolsPanel.tsx
/**
 * DNL Tools Panel Component
 * Phase 1: Custom/Sparse Points System
 * 
 * Collapsible panel within Secondary Line settings providing:
 * - Tick Mode toggle (Regular | Sparse)
 * - Custom Points list with add/edit/remove
 * - Line Labels configuration
 * - Unit Suffix options
 * 
 * Based on DNL Enhancement Specification v1.0, Section 4.1
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Edit2,
  Eye,
  EyeOff,
  HelpCircle,
  Tag,
  Hash,
  ArrowUpDown,
  ToggleLeft,
  ChevronUp,
  Settings,
  Table,
  RefreshCw,
} from 'lucide-react';
import { RootState } from '../../types/redux';
import {
  toggleSparseMode,
  toggleShowZero,
  removeCustomTick,
  toggleCustomTickLabelVisible,
  setCustomTickStyle,
  clearAllCustomTicks,
  setPrimaryLabel,
  setSecondaryLabel,
  togglePrimaryLabelVisible,
  toggleSecondaryLabelVisible,
  setPrimaryUnit,
  setSecondaryUnit,
  togglePrimaryUnitEnabled,
  toggleSecondaryUnitEnabled,
  selectCustomTicksForLine,
  toggleMappingArrows,
  setArrowStyle,
  toggleShowMultiplierLabels,
  setArrowColour,
  generateArrowsFromCustomTicks,
  addMappingArrow,
  removeMappingArrow,
  clearAllMappingArrows,
  toggleRatioTable,
  setTablePositionMode,
  syncTableWithCustomTicks,
  clearRatioTable,
  toggleTableAutoSync,
} from '../../store/slices/dnlToolsSlice';
import type { TablePosition } from '../../types/dnl';
import { AddPointDialog } from './AddPointDialog';
import { EditPointDialog } from './EditPointDialog';
import ViewSwitcher from './ViewSwitcher';
import { ContextPresetSelector } from './ContextPresetSelector';
import { markAsCustom } from '../../store/slices/dnlToolsSlice';

// =============================================================================
// TYPES
// =============================================================================

export interface DNLToolsPanelProps {
  /** Dark mode */
  isDark?: boolean;
  /** Compact layout */
  compact?: boolean;
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

/**
 * Section header with collapse toggle
 */
const SectionHeader: React.FC<{
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  isDark: boolean;
  icon?: React.ReactNode;
}> = ({ title, isOpen, onToggle, isDark, icon }) => (
  <button
    onClick={onToggle}
    className={`w-full flex items-center justify-between py-2 px-1 text-sm font-medium transition-colors rounded ${
      isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
    }`}
  >
    <div className="flex items-center gap-2">
      {icon}
      <span>{title}</span>
    </div>
    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
  </button>
);

/**
 * Toggle switch component
 */
const ToggleSwitch: React.FC<{
  checked: boolean;
  onChange: () => void;
  label: string;
  isDark: boolean;
  disabled?: boolean;
}> = ({ checked, onChange, label, isDark, disabled }) => (
  <label className={`flex items-center justify-between cursor-pointer ${disabled ? 'opacity-50' : ''}`}>
    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
      {label}
    </span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
        checked
          ? 'bg-blue-600'
          : isDark
            ? 'bg-gray-600'
            : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-4.5' : 'translate-x-0.5'
        }`}
        style={{ transform: checked ? 'translateX(18px)' : 'translateX(2px)' }}
      />
    </button>
  </label>
);

/**
 * Custom tick list item
 */
const CustomTickItem: React.FC<{
  tick: {
    id: string;
    value: number;
    label?: string;
    line: 'primary' | 'secondary' | 'both';
    showLabel: boolean;
    style: 'normal' | 'highlighted' | 'question';
  };
  onRemove: () => void;
  onEdit: () => void;
  onToggleLabel: () => void;
  onSetQuestion: () => void;
  onToggleMappingArrow: () => void;
  hasMappingArrow: boolean;
  isDark: boolean;
}> = ({ tick, onRemove, onEdit, onToggleLabel, onSetQuestion, onToggleMappingArrow, hasMappingArrow, isDark }) => {
  // Format value for display
  const displayValue = tick.value.toFixed(4).replace(/\.?0+$/, '');
  const displayLabel = tick.label || displayValue;

  return (
    <div
      className={`flex items-center justify-between py-1.5 px-2 rounded text-sm ${
        isDark ? 'bg-gray-700' : 'bg-gray-100'
      }`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {tick.style === 'question' && (
          <HelpCircle size={14} className="text-amber-500 flex-shrink-0" />
        )}
        <span className={`font-mono truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {displayLabel}
        </span>
        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          ({tick.line})
        </span>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={onEdit}
          className={`p-1 rounded transition-colors ${
            isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
          }`}
          title="Edit point"
        >
          <Edit2 size={14} />
        </button>
        <button
          onClick={onToggleLabel}
          className={`p-1 rounded transition-colors ${
            isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
          }`}
          title={tick.showLabel ? 'Hide label' : 'Show label'}
        >
          {tick.showLabel ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>
        <button
          onClick={onToggleMappingArrow}
          className={`p-1 rounded transition-colors ${
            hasMappingArrow
              ? isDark
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-blue-500 text-white hover:bg-blue-600'
              : isDark
                ? 'hover:bg-gray-600'
                : 'hover:bg-gray-200'
          }`}
          title={hasMappingArrow ? 'Remove mapping arrow' : 'Add mapping arrow'}
        >
          <ArrowUpDown size={14} />
        </button>
        <button
          onClick={onSetQuestion}
          className={`p-1 rounded transition-colors ${
            tick.style === 'question'
              ? 'bg-amber-500 text-white'
              : isDark
                ? 'hover:bg-gray-600'
                : 'hover:bg-gray-200'
          }`}
          title="Toggle unknown value (?)"
        >
          <HelpCircle size={14} />
        </button>
        <button
          onClick={onRemove}
          className={`p-1 rounded transition-colors text-red-500 ${
            isDark ? 'hover:bg-red-900/30' : 'hover:bg-red-100'
          }`}
          title="Remove point"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const DNLToolsPanel: React.FC<DNLToolsPanelProps> = ({
  isDark = false,
  compact = false,
}) => {
  const dispatch = useDispatch();

  // Collapsed sections state
  const [sectionsOpen, setSectionsOpen] = useState({
    contextPreset: true,     // NEW - Open by default
    tickMode: true,
    customPoints: true,
    lineLabels: false,
    mappingArrows: false,
    viewMode: false,
    ratioTable: false,  // NEW
  });

  // Add point dialog state
  const [addPointDialog, setAddPointDialog] = useState<{
    isOpen: boolean;
    targetLine: 'primary' | 'secondary';
    initialValue?: number;
  }>({
    isOpen: false,
    targetLine: 'secondary',
  });

  // Edit point dialog state
  const [editPointDialog, setEditPointDialog] = useState<{
    isOpen: boolean;
    tickId: string | null;
  }>({
    isOpen: false,
    tickId: null,
  });

  // Ref for add arrow input
  const addArrowInputRef = useRef<HTMLInputElement>(null);

  // Redux state
  const dnlTools = useSelector((state: RootState) => state.dnlTools);
  const secondaryLineEnabled = useSelector((state: RootState) => state.secondaryLine.enabled);

  // Get custom ticks for each line
  const primaryTicks = useMemo(
    () => dnlTools.sparseMode.customTicks.filter(t => t.line === 'primary' || t.line === 'both'),
    [dnlTools.sparseMode.customTicks]
  );
  const secondaryTicks = useMemo(
    () => dnlTools.sparseMode.customTicks.filter(t => t.line === 'secondary' || t.line === 'both'),
    [dnlTools.sparseMode.customTicks]
  );

  // Section toggle handler
  const toggleSection = useCallback((section: keyof typeof sectionsOpen) => {
    setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // Styles
  const panelClass = `space-y-3 ${compact ? 'text-sm' : ''}`;
  const sectionClass = `border rounded-lg overflow-hidden ${
    isDark ? 'border-gray-700' : 'border-gray-200'
  }`;
  const visibleSectionClass = `border rounded-lg ${
    isDark ? 'border-gray-700' : 'border-gray-200'
  }`;
  const sectionContentClass = `px-3 py-2 space-y-2 ${
    isDark ? 'bg-gray-800/50' : 'bg-gray-50'
  }`;
  const inputClass = `w-full px-2 py-1 text-sm rounded border focus:outline-none focus:ring-1 focus:ring-blue-500 ${
    isDark
      ? 'bg-gray-700 border-gray-600 text-white'
      : 'bg-white border-gray-300 text-gray-900'
  }`;
  const buttonClass = `px-3 py-1.5 text-sm rounded transition-colors flex items-center gap-1.5 ${
    isDark
      ? 'bg-blue-600 text-white hover:bg-blue-700'
      : 'bg-blue-500 text-white hover:bg-blue-600'
  }`;
  const buttonDangerClass = `px-3 py-1.5 text-sm rounded transition-colors flex items-center gap-1.5 ${
    isDark
      ? 'bg-red-600/20 text-red-400 hover:bg-red-600/30'
      : 'bg-red-50 text-red-600 hover:bg-red-100'
  }`;
  const buttonPrimaryClass = `px-3 py-1.5 text-sm rounded transition-colors flex items-center gap-1.5 ${
    isDark
      ? 'bg-blue-600 text-white hover:bg-blue-700'
      : 'bg-blue-500 text-white hover:bg-blue-600'
  }`;

  // If secondary line is not enabled, show a message
  // NOTE: We now allow access to DNL tools even if secondary line is disabled
  /*
  if (!secondaryLineEnabled) {
    return (
      <div className={`p-3 rounded-lg text-sm text-center ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
        Enable the secondary line to access DNL tools
      </div>
    );
  }
  */

  return (
    <div className={panelClass}>
      {/* Panel Header */}
      <div className={`flex items-center gap-2 pb-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <Hash size={16} className="text-blue-500" />
        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
          DNL Tools
        </span>
      </div>

      {/* Context Preset Section - NEW */}
      <div className={visibleSectionClass}>
        <button
          onClick={() => toggleSection('contextPreset')}
          className={`w-full flex items-center justify-between py-2.5 px-3 transition-colors ${
            sectionsOpen.contextPreset ? 'rounded-t-lg' : 'rounded-lg'
          } ${
            isDark
              ? 'bg-gray-800 hover:bg-gray-750 text-white'
              : 'bg-gray-50 hover:bg-gray-100 text-gray-900'
          }`}
        >
          <div className="flex items-center gap-2">
            <Settings size={16} className="text-blue-500" />
            <span className="font-medium">Quick Setup</span>
          </div>
          {sectionsOpen.contextPreset ? (
            <ChevronUp size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
          ) : (
            <ChevronDown size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
          )}
        </button>

        {sectionsOpen.contextPreset && (
          <div className={`${sectionContentClass} rounded-b-lg`} style={{ display: 'block' }}>
            <ContextPresetSelector isDark={isDark} compact={compact} />
            
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-2`}>
              💡 Choose a preset to auto-configure labels, formats, and scale factor
            </div>
          </div>
        )}
      </div>

      {/* Tick Mode Section */}
      <div className={sectionClass}>
        <SectionHeader
          title="Tick Mode"
          isOpen={sectionsOpen.tickMode}
          onToggle={() => toggleSection('tickMode')}
          isDark={isDark}
        />
        {sectionsOpen.tickMode && (
          <div className={sectionContentClass}>
            {/* Mode Toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => dispatch(toggleSparseMode())}
                className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors ${
                  !dnlTools.sparseMode.enabled
                    ? isDark
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-500 text-white'
                    : isDark
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                Regular
              </button>
              <button
                onClick={() => dispatch(toggleSparseMode())}
                className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors ${
                  dnlTools.sparseMode.enabled
                    ? isDark
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-500 text-white'
                    : isDark
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                Sparse
              </button>
            </div>
            
            {/* Sparse mode options */}
            {dnlTools.sparseMode.enabled && (
              <div className="pt-2 space-y-2">
                <ToggleSwitch
                  checked={dnlTools.sparseMode.showZero}
                  onChange={() => dispatch(toggleShowZero())}
                  label="Always show zero"
                  isDark={isDark}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Custom Points Section */}
      <div className={sectionClass}>
        <SectionHeader
          title={`Custom Points (${dnlTools.sparseMode.customTicks.length})`}
          isOpen={sectionsOpen.customPoints}
          onToggle={() => toggleSection('customPoints')}
          isDark={isDark}
          icon={<Plus size={14} />}
        />
        {sectionsOpen.customPoints && (
          <div className={sectionContentClass}>
            {/* Add Point Button */}
            <button
              onClick={() => setAddPointDialog({ 
                isOpen: true, 
                targetLine: secondaryLineEnabled ? 'secondary' : 'primary' 
              })}
              className={buttonClass}
            >
              <Plus size={14} />
              Add Point
            </button>

            {/* Primary Line Points */}
            {primaryTicks.length > 0 && (
              <div className="space-y-1">
                <div className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Primary Line
                </div>
                {primaryTicks.map(tick => {
                  const hasArrow = dnlTools.mappingArrows.arrows.some(
                    a => Math.abs(a.value - tick.value) < 1e-10
                  );
                  const arrow = dnlTools.mappingArrows.arrows.find(
                    a => Math.abs(a.value - tick.value) < 1e-10
                  );
                  return (
                    <CustomTickItem
                      key={tick.id}
                      tick={tick}
                      onRemove={() => dispatch(removeCustomTick({ id: tick.id, removeLinked: false }))}
                      onEdit={() => setEditPointDialog({ isOpen: true, tickId: tick.id })}
                      onToggleLabel={() => dispatch(toggleCustomTickLabelVisible(tick.id))}
                      onToggleMappingArrow={() => {
                        if (hasArrow && arrow) {
                          dispatch(removeMappingArrow(arrow.id));
                        } else {
                          dispatch(addMappingArrow({ value: tick.value }));
                        }
                      }}
                      hasMappingArrow={hasArrow}
                      onSetQuestion={() =>
                        dispatch(setCustomTickStyle({
                          id: tick.id,
                          style: tick.style === 'question' ? 'normal' : 'question',
                        }))
                      }
                      isDark={isDark}
                    />
                  );
                })}
              </div>
            )}

            {/* Secondary Line Points */}
            {secondaryLineEnabled && secondaryTicks.length > 0 && (
              <div className="space-y-1">
                <div className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Secondary Line
                </div>
                {secondaryTicks.map(tick => {
                  const hasArrow = dnlTools.mappingArrows.arrows.some(
                    a => Math.abs(a.value - tick.value) < 1e-10
                  );
                  const arrow = dnlTools.mappingArrows.arrows.find(
                    a => Math.abs(a.value - tick.value) < 1e-10
                  );
                  return (
                    <CustomTickItem
                      key={tick.id}
                      tick={tick}
                      onRemove={() => dispatch(removeCustomTick({ id: tick.id, removeLinked: false }))}
                      onEdit={() => setEditPointDialog({ isOpen: true, tickId: tick.id })}
                      onToggleLabel={() => dispatch(toggleCustomTickLabelVisible(tick.id))}
                      onToggleMappingArrow={() => {
                        if (hasArrow && arrow) {
                          dispatch(removeMappingArrow(arrow.id));
                        } else {
                          dispatch(addMappingArrow({ value: tick.value }));
                        }
                      }}
                      hasMappingArrow={hasArrow}
                      onSetQuestion={() =>
                        dispatch(setCustomTickStyle({
                          id: tick.id,
                          style: tick.style === 'question' ? 'normal' : 'question',
                        }))
                      }
                      isDark={isDark}
                    />
                  );
                })}
              </div>
            )}

            {/* Empty state */}
            {dnlTools.sparseMode.customTicks.length === 0 && (
              <div className={`text-sm text-center py-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                No custom points yet
              </div>
            )}

            {/* Clear All Button */}
            {dnlTools.sparseMode.customTicks.length > 0 && (
              <button
                onClick={() => dispatch(clearAllCustomTicks())}
                className={buttonDangerClass}
              >
                <Trash2 size={14} />
                Clear All
              </button>
            )}
          </div>
        )}
      </div>

      {/* Line Labels Section */}
      <div className={sectionClass}>
        <SectionHeader
          title="Line Labels"
          isOpen={sectionsOpen.lineLabels}
          onToggle={() => toggleSection('lineLabels')}
          isDark={isDark}
          icon={<Tag size={14} />}
        />
        {sectionsOpen.lineLabels && (
          <div className={sectionContentClass}>
            {/* Primary Line Label */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Primary Label
                </label>
                <button
                  onClick={() => dispatch(togglePrimaryLabelVisible())}
                  className={`p-1 rounded ${isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                >
                  {dnlTools.primaryLabel.visible ? <Eye size={12} /> : <EyeOff size={12} />}
                </button>
              </div>
              <input
                type="text"
                value={dnlTools.primaryLabel.text}
                onChange={e => {
                  dispatch(setPrimaryLabel({ text: e.target.value, visible: true }));
                  dispatch(markAsCustom());  // Mark as custom when user edits
                }}
                placeholder="e.g., Amount (g)"
                className={inputClass}
              />
            </div>

            {/* Secondary Line Label */}
            {secondaryLineEnabled && (
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Secondary Label
                  </label>
                  <button
                    onClick={() => dispatch(toggleSecondaryLabelVisible())}
                    className={`p-1 rounded ${isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                  >
                    {dnlTools.secondaryLabel.visible ? <Eye size={12} /> : <EyeOff size={12} />}
                  </button>
                </div>
                <input
                  type="text"
                  value={dnlTools.secondaryLabel.text}
                  onChange={e => {
                    dispatch(setSecondaryLabel({ text: e.target.value, visible: true }));
                    dispatch(markAsCustom());  // Mark as custom when user edits
                  }}
                  placeholder="e.g., Fat (g)"
                  className={inputClass}
                />
              </div>
            )}

            {/* Unit Suffixes */}
            <div className="pt-2 border-t border-gray-600/30">
              <div className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Unit Suffixes
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Primary
                  </label>
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={dnlTools.primaryUnit.text}
                      onChange={e => dispatch(setPrimaryUnit({ text: e.target.value, enabled: true }))}
                      placeholder="g"
                      className={`${inputClass} w-16`}
                    />
                    <button
                      onClick={() => dispatch(togglePrimaryUnitEnabled())}
                      className={`px-2 rounded text-xs ${
                        dnlTools.primaryUnit.enabled
                          ? 'bg-blue-500 text-white'
                          : isDark
                            ? 'bg-gray-700 text-gray-400'
                            : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {dnlTools.primaryUnit.enabled ? 'On' : 'Off'}
                    </button>
                  </div>
                </div>
                {secondaryLineEnabled && (
                  <div>
                    <label className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      Secondary
                    </label>
                    <div className="flex gap-1">
                      <input
                        type="text"
                        value={dnlTools.secondaryUnit.text}
                        onChange={e => dispatch(setSecondaryUnit({ text: e.target.value, enabled: true }))}
                        placeholder="g"
                        className={`${inputClass} w-16`}
                      />
                      <button
                        onClick={() => dispatch(toggleSecondaryUnitEnabled())}
                        className={`px-2 rounded text-xs ${
                          dnlTools.secondaryUnit.enabled
                            ? 'bg-blue-500 text-white'
                            : isDark
                              ? 'bg-gray-700 text-gray-400'
                              : 'bg-gray-200 text-gray-500'
                        }`}
                      >
                        {dnlTools.secondaryUnit.enabled ? 'On' : 'Off'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mapping Arrows Section */}
      {secondaryLineEnabled && (
        <div className={sectionClass}>
          <SectionHeader
            title="Mapping Arrows"
            isOpen={sectionsOpen.mappingArrows}
            onToggle={() => toggleSection('mappingArrows')}
            isDark={isDark}
            icon={<ArrowUpDown size={14} />}
          />
          {sectionsOpen.mappingArrows && (
            <div className={sectionContentClass}>
              {/* Enable toggle */}
              <ToggleSwitch
                checked={dnlTools.mappingArrows.enabled}
                onChange={() => dispatch(toggleMappingArrows())}
                label="Show arrows"
                isDark={isDark}
              />
              
              {/* Style selector */}
              {dnlTools.mappingArrows.enabled && (
                <>
                  <div className="space-y-1">
                    <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Arrow Style
                    </label>
                    <select
                      value={dnlTools.mappingArrows.defaultStyle}
                      onChange={e => dispatch(setArrowStyle(e.target.value as 'solid' | 'dashed' | 'dotted'))}
                      className={inputClass}
                    >
                      <option value="solid">Solid</option>
                      <option value="dashed">Dashed</option>
                      <option value="dotted">Dotted</option>
                    </select>
                  </div>
                  
                  {/* Show multiplier labels toggle */}
                  <ToggleSwitch
                    checked={dnlTools.mappingArrows.showMultiplierLabels}
                    onChange={() => dispatch(toggleShowMultiplierLabels())}
                    label="Show ×k label"
                    isDark={isDark}
                  />
                  
                  {/* Color picker */}
                  <div className="space-y-1">
                    <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Arrow Color
                    </label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={dnlTools.mappingArrows.defaultColour}
                        onChange={e => dispatch(setArrowColour(e.target.value))}
                        className="h-8 w-16 rounded border cursor-pointer"
                        style={{ borderColor: isDark ? '#4B5563' : '#D1D5DB' }}
                        title="Arrow color"
                      />
                      <input
                        type="text"
                        value={dnlTools.mappingArrows.defaultColour}
                        onChange={e => {
                          // Validate hex color
                          if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) {
                            dispatch(setArrowColour(e.target.value));
                          }
                        }}
                        className={`${inputClass} flex-1`}
                        placeholder="#3B82F6"
                        maxLength={7}
                      />
                    </div>
                  </div>
                  
                  {/* Add arrow input */}
                  <div className="space-y-1">
                    <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Add Arrow at Value
                    </label>
                    <div className="flex gap-1">
                      <input
                        ref={addArrowInputRef}
                        type="number"
                        step="any"
                        placeholder="0"
                        className={`${inputClass} flex-1`}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            const input = e.target as HTMLInputElement;
                            const value = parseFloat(input.value);
                            if (!isNaN(value)) {
                              dispatch(addMappingArrow({ value }));
                              input.value = '';
                            }
                          }
                        }}
                      />
                      <button
                        onClick={() => {
                          if (addArrowInputRef.current) {
                            const value = parseFloat(addArrowInputRef.current.value);
                            if (!isNaN(value)) {
                              dispatch(addMappingArrow({ value }));
                              addArrowInputRef.current.value = '';
                            }
                          }
                        }}
                        className={buttonPrimaryClass}
                        title="Add arrow"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Auto-generate button */}
                  {dnlTools.sparseMode.enabled && (
                    <button
                      onClick={() => dispatch(generateArrowsFromCustomTicks())}
                      className={buttonPrimaryClass}
                    >
                      <Plus size={14} />
                      Generate from Points
                    </button>
                  )}
                  
                  {/* Arrows list */}
                  {dnlTools.mappingArrows.arrows.length > 0 && (
                    <div className="space-y-1 pt-2 border-t border-gray-600/30">
                      <div className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Arrows ({dnlTools.mappingArrows.arrows.length})
                      </div>
                      {dnlTools.mappingArrows.arrows.map(arrow => (
                        <div
                          key={arrow.id}
                          className={`flex items-center justify-between py-1.5 px-2 rounded text-sm ${
                            isDark ? 'bg-gray-700' : 'bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div
                              className="w-4 h-4 rounded border flex-shrink-0"
                              style={{ backgroundColor: arrow.colour }}
                              title={arrow.colour}
                            />
                            <span className={`font-mono truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {arrow.value.toFixed(4).replace(/\.?0+$/, '')}
                            </span>
                            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              ({arrow.style})
                            </span>
                          </div>
                          <button
                            onClick={() => dispatch(removeMappingArrow(arrow.id))}
                            className={`p-1 rounded transition-colors text-red-500 ${
                              isDark ? 'hover:bg-red-900/30' : 'hover:bg-red-100'
                            }`}
                            title="Remove arrow"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      
                      {/* Clear all button */}
                      <button
                        onClick={() => dispatch(clearAllMappingArrows())}
                        className={buttonDangerClass}
                      >
                        <Trash2 size={14} />
                        Clear All Arrows
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Ratio Table Section - NEW */}
      {secondaryLineEnabled && (
        <div className={sectionClass}>
          <button
            onClick={() => toggleSection('ratioTable')}
            className={`w-full flex items-center justify-between py-2.5 px-3 transition-colors rounded-t-lg ${
              isDark
                ? 'bg-gray-800 hover:bg-gray-750 text-white'
                : 'bg-gray-50 hover:bg-gray-100 text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <Table size={16} className="text-green-500" />
              <span className="font-medium">Ratio Table</span>
            </div>
            {sectionsOpen.ratioTable ? (
              <ChevronUp size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
            ) : (
              <ChevronDown size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
            )}
          </button>

          {sectionsOpen.ratioTable && (
            <div className={sectionContentClass}>
              {/* Show/Hide Toggle */}
              <ToggleSwitch
                checked={dnlTools.ratioTable.visible}
                onChange={() => dispatch(toggleRatioTable())}
                label="Show table"
                isDark={isDark}
              />
              
              {/* Auto-sync Toggle */}
              <ToggleSwitch
                checked={dnlTools.ratioTable.autoSync}
                onChange={() => dispatch(toggleTableAutoSync())}
                label="Auto-sync with points"
                isDark={isDark}
                disabled={!dnlTools.ratioTable.visible}
              />
              
              {/* Position Selector */}
              {dnlTools.ratioTable.visible && (
                <div className="space-y-1">
                  <label className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Position
                  </label>
                  <select
                    value={dnlTools.ratioTable.positionMode}
                    onChange={e => dispatch(setTablePositionMode(e.target.value as TablePosition))}
                    className={inputClass}
                  >
                    <option value="top-left">Top Left</option>
                    <option value="top-right">Top Right</option>
                    <option value="bottom-left">Bottom Left</option>
                    <option value="bottom-right">Bottom Right</option>
                    <option value="custom">Custom (drag to move)</option>
                  </select>
                </div>
              )}
              
              {/* Actions */}
              {dnlTools.ratioTable.visible && (
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => dispatch(syncTableWithCustomTicks())}
                    className={buttonClass}
                    disabled={dnlTools.ratioTable.autoSync}
                  >
                    <RefreshCw size={14} />
                    Sync Now
                  </button>
                  <button
                    onClick={() => dispatch(clearRatioTable())}
                    className={buttonDangerClass}
                  >
                    <Trash2 size={14} />
                    Clear
                  </button>
                </div>
              )}
              
              <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-2`}>
                💡 Table shows value pairs from custom points. Drag to reposition.
              </div>
            </div>
          )}
        </div>
      )}

      {/* VIEW MODE Section */}
      <div className={sectionClass}>
        <button
          onClick={() => toggleSection('viewMode')}
          className={`w-full px-3 py-2 flex items-center justify-between ${
            isDark
              ? 'bg-gray-800 hover:bg-gray-750 text-white'
              : 'bg-gray-50 hover:bg-gray-100 text-gray-900'
          }`}
        >
          <div className="flex items-center gap-2">
            <ToggleLeft size={16} className="text-purple-500" />
            <span className="font-medium">View Mode</span>
          </div>
          {sectionsOpen.viewMode ? (
            <ChevronUp size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
          ) : (
            <ChevronDown size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
          )}
        </button>

        {sectionsOpen.viewMode && (
          <div className={sectionContentClass}>
            <ViewSwitcher isDark={isDark} compact={compact} />
          </div>
        )}
      </div>

      {/* Add Point Dialog */}
      <AddPointDialog
        isOpen={addPointDialog.isOpen}
        onClose={() => setAddPointDialog({ ...addPointDialog, isOpen: false })}
        initialLine={addPointDialog.targetLine}
        initialValue={addPointDialog.initialValue}
        isDark={isDark}
      />

      {/* Edit Point Dialog */}
      <EditPointDialog
        isOpen={editPointDialog.isOpen}
        onClose={() => setEditPointDialog({ isOpen: false, tickId: null })}
        tick={editPointDialog.tickId ? dnlTools.sparseMode.customTicks.find(t => t.id === editPointDialog.tickId) || null : null}
        isDark={isDark}
      />
    </div>
  );
};

export default DNLToolsPanel;
